import { ActiveItem, InventoryQuantity } from "./InventoryContext";
import { ItemList } from "./GameData";

/**
 * Normalizes an active item (miner, smelter, power machine) to ensure all required fields are present.
 *
 * @param item - The item to normalize (can be partial)
 * @returns Fully normalized ActiveItem with all fields
 *
 * @example
 * const normalized = normalizeActiveItem({ itemId: 'miner1', running: true });
 * // Returns: { itemId: 'miner1', running: true, progressMs: 0, fuelStarved: false }
 */
export const normalizeActiveItem = (
    item: ActiveItem | { itemId: string; running: boolean; progressMs?: number }
): ActiveItem => ({
    itemId: item.itemId,
    running: !!item.running,
    progressMs: typeof item.progressMs === "number" ? item.progressMs : 0,
    fuelStarved: "fuelStarved" in item ? !!(item as ActiveItem).fuelStarved : false,
    fuelBuffer: "fuelBuffer" in item ? (item as ActiveItem).fuelBuffer ?? 0 : 0,
});

/**
 * Ensures a field exists in an array of fields, adding it with default values if missing.
 * Useful for ensuring miner/smelter fields are initialized for all resource types.
 *
 * @param fields - Array of field objects
 * @param resourceType - The resource type to ensure exists
 * @param defaults - Function that returns default field values if field doesn't exist
 * @returns Updated fields array (original array if field exists, new array with field added if not)
 *
 * @example
 * const updatedMiners = ensureField(
 *   minerState,
 *   'coal',
 *   () => ({ resourceType: 'coal', miners: [], clearedSpots: 1 })
 * );
 */
export const ensureField = <T extends { resourceType: string }>(
    fields: T[],
    resourceType: string,
    defaults: () => T
): T[] => {
    const existing = fields.find((field) => field.resourceType === resourceType);
    if (existing) return fields;
    return [...fields, defaults()];
};

/**
 * Ensures all items from ItemList exist in the inventory array with at least 0 quantity.
 * Prevents errors when trying to access items that haven't been added to inventory yet.
 *
 * @param inventory - Current inventory array
 * @returns Normalized inventory with all items from ItemList
 *
 * @example
 * const normalized = ensureInventoryItems(currentInventory);
 * // Guarantees every item in ItemList exists in the result
 */
export const ensureInventoryItems = (inventory: InventoryQuantity[]): InventoryQuantity[] => {
    const normalized = [...inventory];
    ItemList.forEach((item) => {
        const existing = normalized.find((entry) => entry.item.id === item.id);
        if (!existing) {
            normalized.push({ item, quantity: 0 });
        }
    });
    return normalized;
};

/**
 * Finds an inventory entry by item ID.
 *
 * @param currentInventory - The current inventory array
 * @param itemId - The ID of the item to find
 * @returns The inventory entry if found, undefined otherwise
 *
 * @example
 * const coalEntry = findInventoryQuantity(inventory, 'coal');
 * console.log(coalEntry?.quantity); // 42
 */
export const findInventoryQuantity = (
    currentInventory: InventoryQuantity[],
    itemId: string
): InventoryQuantity | undefined => {
    return currentInventory.find((inv) => inv.item.id === itemId);
};

/**
 * Checks if the inventory has at least the specified quantity of an item.
 *
 * @param currentInventory - The current inventory array
 * @param itemId - The ID of the item to check
 * @param requiredQuantity - The minimum quantity required (defaults to 1)
 * @returns True if inventory has at least requiredQuantity of the item, false otherwise
 *
 * @example
 * if (hasInventoryQuantity(inventory, 'iron', 10)) {
 *   // Player has at least 10 iron
 * }
 */
export const hasInventoryQuantity = (
    currentInventory: InventoryQuantity[],
    itemId: string,
    requiredQuantity: number = 1
): boolean => {
    const entry = findInventoryQuantity(currentInventory, itemId);
    return entry ? entry.quantity >= requiredQuantity : false;
};
